import { useEffect, useState } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import {
  FaTrash, FaDownload, FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel,
  FaFilePowerpoint, FaFileArchive, FaFileCsv, FaFileVideo, FaEnvelope, FaImage, FaChevronLeft, FaChevronRight, FaPencilAlt, FaTimes, FaLink,
} from "react-icons/fa";
import { errorMessage, fileContentUrl } from "../../services/apiClient.js";

// THE file tile — one component, used by every surface that shows files.
//
// Lifted out of ClaimFilesTab on 2026-08-06 when the Inspection tab needed the
// same thing (Robert: "the file tab works with it and displays properly, we
// should use the same system here"). A second renderer would have meant keeping
// the type icons, the thumbnail fallback, the not-held state and the download
// rules in step by hand, which is the failure the JobList columns comment
// already describes.
//
// It knows how to show an image, a typed icon for a document (PDF, Word, Excel,
// EMAIL, video, archive), and a file whose BYTES are missing — the legacy-import
// state where the row exists and the file does not.

function humanSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Broken/absent IMAGE → generic "preview unavailable".
function Placeholder({ isImage, large }) {
  const Icon = isImage ? FaImage : FaFileAlt;
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-muted h-100 w-100">
      <Icon size={large ? 64 : 34} className="opacity-50" />
      <span className={large ? "mt-2" : "small mt-1"}>Preview unavailable</span>
    </div>
  );
}

// Non-image file → a recognisable type icon (PDF/Word/Excel/…) instead of a bare
// placeholder. Resolves by extension first, then the (Base4-imported) content_type.
function fileVisual(file) {
  if (file.external_url) return { Icon: FaLink, cls: "text-primary", label: "Link" };
  const ext = (file.extension || "").toLowerCase();
  const ct = (file.content_type || "").toLowerCase();
  if (["msg", "eml"].includes(ext) || ct.includes("message/") || ct.includes("outlook"))
    return { Icon: FaEnvelope, cls: "text-info", label: "Email" };
  if (["mp4", "mov", "avi", "wmv", "mkv", "m4v"].includes(ext) || ct.startsWith("video/"))
    return { Icon: FaFileVideo, cls: "text-primary", label: "Video" };
  if (ext === "pdf" || ct.includes("pdf")) return { Icon: FaFilePdf, cls: "text-danger", label: "PDF" };
  if (["doc", "docx", "odt", "rtf"].includes(ext) || ct.includes("word") || ct.includes("opendocument.text"))
    return { Icon: FaFileWord, cls: "text-primary", label: "Word" };
  if (["xls", "xlsx", "ods"].includes(ext) || ct.includes("spreadsheet") || ct.includes("ms-excel"))
    return { Icon: FaFileExcel, cls: "text-success", label: "Excel" };
  if (ext === "csv" || ct.includes("csv")) return { Icon: FaFileCsv, cls: "text-success", label: "CSV" };
  if (["ppt", "pptx", "odp"].includes(ext) || ct.includes("presentation") || ct.includes("powerpoint"))
    return { Icon: FaFilePowerpoint, cls: "text-warning", label: "PowerPoint" };
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return { Icon: FaFileArchive, cls: "text-secondary", label: ext.toUpperCase() };
  return { Icon: FaFileAlt, cls: "text-secondary", label: "File" };
}

function DocPreview({ file, large }) {
  const { Icon, cls, label } = fileVisual(file);
  return (
    <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
      <Icon size={large ? 72 : 44} className={cls} />
      <span className={`text-muted ${large ? "mt-2" : "small mt-1"}`}>{label}</span>
    </div>
  );
}

// The name split for renaming: the BASE is the user's to edit, the extension is
// not — it says what the file is and must agree with content_type, so it shows
// as a fixed suffix beside the input and travels back with whatever was typed
// (the server re-appends it anyway; this just makes that visible).
function splitName(file) {
  const name = file.original_filename || "";
  const ext = (file.extension || "").trim();
  if (ext && name.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    return { base: name.slice(0, -(ext.length + 1)), suffix: name.slice(-(ext.length + 1)) };
  }
  return { base: name, suffix: "" };
}

function FileCard({ file, onOpen, onDelete, onRename }) {
  // Prefer the thumbnail, keep the original as a fallback. Two ids, not one,
  // because a thumbnail ROW can exist on a box that does not have its BYTES:
  // `./deploy.sh --db` copies the database between machines and the storage root
  // does not travel with it. Collapsing these into one id meant that state
  // rendered a broken tile over a file that was sitting on the disk all along.
  const originalId = file.has_bytes ? file.id : null;
  const [previewId, setPreviewId] = useState(file.thumbnail_file_id || originalId);
  const [broken, setBroken] = useState(!file.is_image || !previewId);
  const visual = fileVisual(file);
  const clickable = file.is_image && previewId && !broken;

  // Inline rename (1.0 had it, the tile is where people look for it). Offered
  // only when the surface passes onRename — a pencil with no handler behind it
  // would be a dead control.
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState("");
  const { base, suffix } = splitName(file);

  function startRename() {
    setDraft(base);
    setRenameError("");
    setRenaming(true);
  }
  async function saveRename() {
    const typed = draft.trim();
    if (!typed || typed === base) {
      setRenaming(false);
      return;
    }
    setRenameBusy(true);
    setRenameError("");
    try {
      await onRename(file, typed + suffix);
      setRenaming(false);
    } catch (err) {
      setRenameError(errorMessage(err, "Unable to rename."));
    } finally {
      setRenameBusy(false);
    }
  }

  return (
    <Card className="h-100 shadow-sm">
      <div
        /* EXPERIMENT — the Zip All animation measures these to lift each photo off
           its own place on the page. Read only, by attribute; nothing here depends
           on it and removing it just makes that animation start on the cylinder
           instead. See src/experiments/photo-helix/README.md. */
        data-ph-tile={file.id}
        className={`bg-body-tertiary d-flex align-items-center justify-content-center overflow-hidden ${clickable ? "clickable" : ""}`}
        style={{ height: 150 }}
        onClick={clickable ? () => onOpen(file) : undefined}
        data-hint={clickable ? "Click to view full screen" : undefined}
      >
        {clickable ? (
          <img
            src={fileContentUrl(previewId)}
            alt={file.original_filename}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => {
              // Fall back to the full-size original before declaring it broken —
              // slow to paint, but it is the real photo and it is here.
              if (originalId && previewId !== originalId) setPreviewId(originalId);
              else setBroken(true);
            }}
          />
        ) : file.external_url ? (
          // AN EXTERNAL LINK (Ruby, 2026-08-24: the YouTube walkthroughs) —
          // the bytes live on someone else's site, so the tile IS the link:
          // new tab, the host shown so nobody clicks blind.
          <a
            href={file.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="d-flex flex-column align-items-center justify-content-center text-decoration-none w-100 h-100"
            data-hint={file.external_url}
          >
            <FaLink size={34} className="text-primary mb-1" />
            <span className="small text-muted text-truncate px-2" style={{ maxWidth: "100%" }}>
              {(() => { try { return new URL(file.external_url).hostname.replace(/^www\./, ""); } catch { return "link"; } })()}
            </span>
          </a>
        ) : file.has_bytes && !file.is_image ? (
          <DocPreview file={file} />
        ) : (
          // Bytes absent (or a broken/thumbless image) → clearly "not here", so a PDF
          // that hasn't been imported never looks like it exists.
          <Placeholder isImage={file.is_image} />
        )}
      </div>
      <Card.Body className="p-2">
        {renaming ? (
          <>
            {/* Same keyboard contract as the sidebar's QuickFact: Enter saves,
                Escape cancels, explicit buttons for mouse users. */}
            <div className="d-flex align-items-center gap-1">
              <Form.Control
                size="sm"
                value={draft}
                autoFocus
                disabled={renameBusy}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveRename();
                  if (e.key === "Escape") setRenaming(false);
                }}
                aria-label="File name"
              />
              {suffix ? <span className="text-muted small text-nowrap">{suffix}</span> : null}
            </div>
            {renameError ? <div className="small text-danger">{renameError}</div> : null}
            <div className="d-flex gap-1 mt-1">
              <Button size="sm" className="py-0" onClick={saveRename} disabled={renameBusy}>Save</Button>
              <Button size="sm" className="py-0" variant="secondary" onClick={() => setRenaming(false)} disabled={renameBusy}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="small text-truncate fw-semibold" data-hint={file.original_filename}>
              {!file.is_image ? <visual.Icon className={`me-1 ${visual.cls}`} /> : null}
              {file.original_filename || "(unnamed)"}
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <span className="text-muted" style={{ fontSize: "0.72rem" }}>{humanSize(file.size_bytes)}</span>
              <span className="text-nowrap">
                {onRename ? (
                  <Button variant="outline-secondary" size="sm" className="py-0 px-1 me-1" data-hint="Rename" aria-label="Rename" onClick={startRename}>
                    <FaPencilAlt size={11} />
                  </Button>
                ) : null}
                <a
                  className={`btn btn-sm btn-outline-secondary py-0 px-1 me-1 ${file.has_bytes || file.external_url ? "" : "disabled"}`}
                  href={file.external_url || (file.has_bytes ? fileContentUrl(file.id, { download: true }) : undefined)}
                  target={file.external_url ? "_blank" : undefined}
                  rel={file.external_url ? "noopener noreferrer" : undefined}
                  data-hint={file.external_url ? "Open link" : file.has_bytes ? "Download" : "File bytes not available"} aria-label={file.external_url ? "Open link" : file.has_bytes ? "Download" : "File bytes not available"}
                >
                  <FaDownload size={11} />
                </a>
                <Button variant="outline-danger" size="sm" className="py-0 px-1" data-hint="Delete" aria-label="Delete" onClick={() => onDelete(file)}>
                  <FaTrash size={11} />
                </Button>
              </span>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// Full-screen viewer (legacy PhotoView.aspx): image scaled to fit, keyboard + arrows,
// prev/next across all images on the claim.
function Lightbox({ images, index, onClose, onNav }) {
  const [broken, setBroken] = useState(false);
  const file = images[index];

  useEffect(() => setBroken(false), [index]);
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onNav(-1);
      else if (e.key === "ArrowRight") onNav(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  if (!file) return null;
  const canPrev = index > 0;
  const canNext = index < images.length - 1;

  return (
    <Modal show fullscreen onHide={onClose} contentClassName="bg-black border-0">
      {/* data-bs-theme="dark" — the ONE place in the app that carries it.
          ClaimsDesk offers only light themes, so every component assumes a pale
          background and reaches for dark ink. This viewer is the exception: it
          is deliberately black so the photo is the only lit thing on screen, and
          controls that inherited the page's assumptions came out dark-on-dark
          (Robert 2026-08-07).
          Scoping the token scale rather than hand-picking a "-light" variant per
          control is what makes that durable — every Bootswatch theme ships a
          [data-bs-theme=dark] block (--bs-emphasis-color flips #000 → #fff), so
          anything added here later is correct by construction instead of by
          somebody remembering. It changes nothing outside this modal. */}
      <Modal.Body
        data-bs-theme="dark"
        className="p-0 d-flex flex-column bg-black position-relative"
      >
        {/* Near-opaque, not 60%. The two controls on this strip are OUTLINE
            buttons — a border and nothing behind it — so at 60% their contrast
            was set by whatever the photo underneath happened to be, and a light
            image showing through left them reading as grey rather than white. */}
        <div className="d-flex justify-content-between align-items-center px-3 py-2 text-white" style={{ background: "rgba(0,0,0,0.85)" }}>
          <span className="text-truncate small">{file.original_filename} <span className="opacity-50">· {index + 1} of {images.length}</span></span>
          <span className="d-flex gap-2">
            {/* SOLID light, not outline. An outline button is a border with
                nothing behind it, so on this screen its contrast was decided by
                whatever the photo underneath happened to be — which is how the
                only two controls in a full-screen viewer came to be hard to find
                (Robert 2026-08-07, twice: it was still wrong after the backdrop
                was darkened). A filled button carries its own background and is
                the same to read over a black surround, a night photo of a roof
                cavity, or a white ceiling. */}
            {file.has_bytes ? (
              <a className="btn btn-sm btn-light" href={fileContentUrl(file.id, { download: true })} data-hint="Download" aria-label="Download"><FaDownload /></a>
            ) : null}
            <Button variant="light" size="sm" onClick={onClose} data-hint="Close (Esc)" aria-label="Close (Esc)"><FaTimes /></Button>
          </span>
        </div>

        <div className="flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden position-relative">
          {file.has_bytes && !broken ? (
            <img
              src={fileContentUrl(file.id)}
              alt={file.original_filename}
              style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }}
              onError={() => setBroken(true)}
            />
          ) : (
            <div className="text-white-50"><Placeholder isImage large /></div>
          )}

          {/* LIGHT, not dark. These sat at `variant="dark"` on a black backdrop
              at 75% opacity — a dark grey button on black, which is the paging
              control being invisible until you happen to mouse over it. White at
              75% reads against both the black surround and a photo. */}
          {canPrev ? (
            <Button variant="light" className="position-absolute top-50 start-0 translate-middle-y ms-2 opacity-75" onClick={() => onNav(-1)} data-hint="Previous (←)" aria-label="Previous (←)"><FaChevronLeft /></Button>
          ) : null}
          {canNext ? (
            <Button variant="light" className="position-absolute top-50 end-0 translate-middle-y me-2 opacity-75" onClick={() => onNav(1)} data-hint="Next (→)" aria-label="Next (→)"><FaChevronRight /></Button>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );
}


export { FileCard, DocPreview, Placeholder, Lightbox, fileVisual, humanSize };
export default FileCard;
